import React from "react";
import { getAllActivePublications } from "@plugins/gatsby-source-paligo/src/config";
import { PublicationsConfigEntry } from "@plugins/gatsby-source-paligo/src/types";
import { useSearchModalContext } from "contexts/SearchModalContext";
import { useSiteContext } from "contexts/SiteContext";
import { useAlgoliaRefinements } from "hooks/use-algolia-refinements";
import { useUrlParams } from "hooks/use-url-params";
import { useRefinementList } from "react-instantsearch-hooks";

import { VersionSelector } from "components/VersionSelector/VersionSelector";

import { parseDocumentPath } from "utils/url";
import { getVersionSelectorValueFromPath } from "utils/version-selector";

const publications = getAllActivePublications();

const getSearchFilters = (productFromUrl: string) => {
    if (productFromUrl === "all") {
        return { product: "all", version: "all" };
    }

    const [productSlug, versionSlug] = productFromUrl.split("_");

    const publicationConfig = publications.find(
        ({ slug }) => slug === productSlug
    ) as PublicationsConfigEntry;

    return {
        product: publicationConfig.name,
        version: versionSlug.substring(1), // remove first v
    };
};

export function SearchVersionSelector() {
    const [loading, setLoading] = React.useState<boolean>(false);
    const { state, dispatch } = useSearchModalContext();
    const { pathname } = useSiteContext();

    const { refine: refineProduct } = useRefinementList({
        attribute: "product",
    });
    const { refine: refineVersion } = useRefinementList({
        attribute: "version",
    });

    const currentProductRefinements = useAlgoliaRefinements("product");
    const currentVersionRefinements = useAlgoliaRefinements("version");

    const { getUrlParam, setUrlParams } = useUrlParams();

    React.useEffect(() => {
        const productFromUrl = getUrlParam("product");

        // if we specify it as a param use that, if not calculate from the URL
        if (productFromUrl) {
            dispatch({
                type: "set_version_select_value",
                value: productFromUrl,
            });
        }
    }, [getUrlParam, dispatch]);

    React.useEffect(() => {
        setLoading(false);
    }, [state.hits]);

    React.useEffect(() => {
        if (state.searchModalOpen) {
            // on initial render apply the required filters and set the URL param
            const { product, version } = getSearchFilters(
                state.versionSelectValue
            );

            // if the current version selector value matches the URL params do
            // nothing
            if (
                currentProductRefinements.length === 1 &&
                currentProductRefinements[0] === product &&
                currentVersionRefinements.length === 1 &&
                currentVersionRefinements[0] === version
            ) {
                return;
            }

            // remove any existing product/version refinements
            for (const currentProductRefinement of currentProductRefinements) {
                refineProduct(currentProductRefinement as string);
            }
            for (const currentVersionRefinement of currentVersionRefinements) {
                refineVersion(currentVersionRefinement as string);
            }

            if (state.versionSelectValue !== "all") {
                refineProduct(product);
                refineVersion(version);
            }

            setUrlParams({ product: state.versionSelectValue });
        }
    }, [
        state.searchModalOpen,
        state.versionSelectValue,
        currentProductRefinements,
        currentVersionRefinements,
        refineProduct,
        refineVersion,
        setUrlParams,
    ]);

    React.useEffect(() => {
        if (!state.searchModalOpen) {
            // reset to default
            const pathInfo = parseDocumentPath(pathname);
            const versionSelectValue = getVersionSelectorValueFromPath({
                pathInfo,
            });

            dispatch({
                type: "set_version_select_value",
                value: versionSelectValue,
            });
        }
    }, [state.searchModalOpen, pathname, dispatch]);

    const handleValueChange = (value: string) => {
        dispatch({ type: "set_version_select_value", value });
        if (state.searchTerm) {
            setLoading(true);
        }
    };

    return (
        <VersionSelector
            hasAllOption
            value={state.versionSelectValue}
            loading={loading}
            onValueChange={handleValueChange}
        />
    );
}
